/**
 * Setup — written once, worded by the blueprint.
 *
 * Every word a user reads on this screen comes from the blueprint record, so
 * Hire asks about candidates and Post-Mortem asks what contributed, from the
 * same component. The method is fixed by the blueprint rather than chosen,
 * because the whole promise of a job-shaped app is that the method question is
 * already answered — the Agent explains the choice instead of delegating it.
 */

import { useEffect, useMemo, useState } from 'react';
import { uniqueLines } from '@/lib/uniqueLines';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { SESSION_CREATE_BALL, SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, StatusChip } from '../components/primitives';
import { GuideMomentCard } from '../components/flow';
import { useToast } from '@/components/ui/Toast';
import {
  BLUEPRINT_WINDOW_DAYS,
  blueprintCriteriaFromText,
  blueprintCriteriaProblem,
  blueprintWindow,
  createBlueprintSession,
  isBlueprintSession,
} from './blueprintActions';
import type { AppBlueprint } from './types';

const FIELD_CLASS =
  'mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]';

export default function SetupScreen({ blueprint }: { blueprint: AppBlueprint }) {
  const app = getAppDefinition(blueprint.appId)!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const mayCreateSession = useCreateRights().session;
  const { showToast } = useToast();
  const sessions = useSessionStore((s) => s.sessions);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);

  const [name, setName] = useState('');
  const [candidatesText, setCandidatesText] = useState('');
  // Starts as the blueprint's suggested list, as real text — not a placeholder —
  // so an owner who agrees with it can start without retyping anything.
  const [criteriaText, setCriteriaText] = useState(() =>
    (blueprint.criteria?.suggested ?? []).join('\n'),
  );
  const [busy, setBusy] = useState(false);
  // S-9: an offline blueprint runs in a window; answers close at its end.
  const [windowDays, setWindowDays] = useState<number>(BLUEPRINT_WINDOW_DAYS[0]);
  const [error, setError] = useState<string | null>(null);

  const { copy, guidance } = blueprint;

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  const candidates = useMemo(
    () => uniqueLines(candidatesText.split('\n')),
    [candidatesText],
  );

  const mine = useMemo(
    () =>
      sessions
        .filter((s) => s.facilitatorId === userId && isBlueprintSession(s, blueprint.appId))
        .sort(
          (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
        ),
    [sessions, userId, blueprint.appId],
  );

  const criteria = useMemo(() => blueprintCriteriaFromText(criteriaText), [criteriaText]);
  const criteriaProblem = blueprintCriteriaProblem(blueprint, criteria);

  const enough = candidates.length >= blueprint.minimumCandidates;
  const canCreate = name.trim().length > 0 && enough && !criteriaProblem && !busy;
  const methodStep = blueprint.criteria ? 4 : 3;

  const create = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const result = await createBlueprintSession({
      blueprint,
      name: name.trim(),
      candidates,
      criteria,
      facilitatorId: userId,
      windowDays,
      toast: showToast,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'That could not be created. Try again.');
      return;
    }
    navigate(`${app.routeFamily}/${result.sessionId}`);
  };

  return (
    <AppShell app={app}>
      <ScreenHeading title={app.name} subtitle={app.tagline} />
      <BallLine ballWith={mayCreateSession ? 'you — set it up' : SESSION_CREATE_BALL} />

      <div className="space-y-4">
        {/* Creating inserts into prioritization_sessions, whose insert rule
            (migration 754) takes SESSION_RUN: the form is drawn only for its
            holders, the line for everyone else (build contract §2.8). */}
        {mayCreateSession ? (
          <>
            <AppCard>
              <Eyebrow>1 · {copy.questionLabel}</Eyebrow>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={copy.questionPlaceholder}
                className={FIELD_CLASS}
              />
            </AppCard>

            <AppCard>
              <Eyebrow>2 · {copy.candidateLabel}</Eyebrow>
              <textarea
                value={candidatesText}
                onChange={(e) => setCandidatesText(e.target.value)}
                rows={7}
                placeholder={copy.candidatePlaceholder}
                className={FIELD_CLASS}
              />
              <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {candidates.length}{' '}
                {candidates.length === 1 ? copy.candidateNoun : copy.candidateNounPlural}
                {enough ? ' — ' : ` — you need at least ${blueprint.minimumCandidates}. `}
                {enough ? copy.candidateHint : ''}
              </p>
            </AppCard>

            {blueprint.criteria ? (
              <AppCard>
                <Eyebrow>3 · {blueprint.criteria.label}</Eyebrow>
                <textarea
                  value={criteriaText}
                  onChange={(e) => setCriteriaText(e.target.value)}
                  rows={5}
                  aria-label={blueprint.criteria.label}
                  placeholder="One per line"
                  className={FIELD_CLASS}
                />
                {criteriaProblem ? (
                  <p className="mt-1 font-body text-xs text-[var(--coral-text)]">
                    {criteriaProblem}
                  </p>
                ) : (
                  <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    {criteria.length} {criteria.length === 1 ? 'line' : 'lines'} — {blueprint.criteria.hint}
                  </p>
                )}
              </AppCard>
            ) : null}

            <div>
              <div className="mb-2">
                <Eyebrow>{methodStep} · How you will decide</Eyebrow>
              </div>
              <GuideMomentCard
                question="Which method fits this?"
                recommendation={blueprint.methodName}
                why={guidance.methodWhy}
                acceptLabel={canCreate ? 'Use it and start' : 'Use this method'}
                onAccept={() => {
                  if (canCreate) void create();
                }}
              />
            </div>

            {blueprint.timing === 'async' ? (
              <AppCard>
                <Eyebrow>{methodStep + 1} · When answers close</Eyebrow>
                <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                  People answer in their own time until then. After it, no answer can be given, and you read the result.
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="blueprint-window">
                  {[...BLUEPRINT_WINDOW_DAYS].sort((a, b) => a - b).map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => setWindowDays(days)}
                      aria-pressed={windowDays === days}
                      className={`rounded-full px-3 py-1.5 font-body text-xs ${
                        windowDays === days
                          ? 'bg-primary-600 text-white'
                          : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
                      }`}
                    >
                      {days === 1 ? 'In 1 day' : days === 7 ? 'In a week' : `In ${days} days`}
                    </button>
                  ))}
                </div>
                <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                  Closes {blueprintWindow(blueprint, windowDays)?.closesAt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
                </p>
              </AppCard>
            ) : null}

            {error ? (
              <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end">
              <PrimaryButton onClick={() => void create()} disabled={!canCreate}>
                <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Start'}
              </PrimaryButton>
            </div>
          </>
        ) : (
          <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-create-refused">
            {SESSION_CREATE_LINE}
          </p>
        )}

        {mine.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>Yours</Eyebrow>
            </div>
            <ul className="mt-2">
              {mine.slice(0, 8).map((s) => (
                <li
                  key={s.id}
                  className="border-b border-gray-100 last:border-0 dark:border-[var(--rule)]"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`${app.routeFamily}/${s.id}`)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {s.name}
                    </span>
                    <StatusChip
                      label={
                        s.state === 'PUBLISHED'
                          ? 'Published'
                          : s.state === 'RUN_COMPLETE'
                            ? 'Result ready'
                            : s.state === 'CRITERIA_FINALIZED'
                              ? 'Open'
                              : 'Draft'
                      }
                      tone={s.state === 'DRAFT' ? 'neutral' : 'green'}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </AppCard>
        ) : (
          <EmptyState
            title={`Nothing here yet`}
            hint={`Set one up above — it takes about a minute.`}
          />
        )}
      </div>
    </AppShell>
  );
}
